import { gql, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { FormEvent, useState } from 'react';

import { Form } from 'src/components/common/form/Form';
import {
  checkServerErrorForPasswordFailure,
  usePasswordChecker,
} from 'src/hooks/usePasswordChecker';
import { FormErrors } from 'src/lib/forms/FormErrors';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const PasswordCardSettingsFragment = gql`
  fragment PasswordCardSettingsFragment on User {
    id
    synchronized
  }
`;

const ChangePasswordMutation = gql`
  mutation UI__PasswordCardChangePasswordMutation(
    $userId: ID!
    $oldPassword: String!
    $newPassword: String!
  ) {
    user(id: $userId) {
      changePassword(newPassword: $newPassword, previousPassword: $oldPassword)
    }
  }
`;

interface PasswordData {
  oldPassword: string;
  newPassword: string;
}

const validate = (values: PasswordData): FormErrors<PasswordData> => ({
  ...(!values.oldPassword
    ? { oldPassword: 'Please provide your previous password.' }
    : {}),
  ...(!values.newPassword
    ? { newPassword: 'Please provide a new password.' }
    : values.newPassword.length < 8
      ? {
          newPassword:
            'Your password is too short and must be at least 8 characters.',
        }
      : {}),
});

const emptyState: PasswordData = {
  oldPassword: '',
  newPassword: '',
};

interface Props {
  userId: string;
}
export function PasswordCard({ userId }: Props) {
  const [modalVisible, setModalVisible] = useState(false);
  const [values, setValues] = useState<PasswordData>(emptyState);
  const [errors, setErrors] = useState<
    FormErrors<PasswordData & { global?: string }>
  >({});

  const [changePassword, { loading }] = useMutation<
    GraphQLTypes.UI__PasswordCardChangePasswordMutation,
    GraphQLTypes.UI__PasswordCardChangePasswordMutationVariables
  >(ChangePasswordMutation, {
    variables: {
      userId,
      newPassword: values.newPassword,
      oldPassword: values.oldPassword,
    },
    onCompleted: () => setModalVisible(false),
    onError: (e) => {
      const weakPasswordCheck = checkServerErrorForPasswordFailure(e.message);
      if (!weakPasswordCheck.isSuccess) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          newPassword: weakPasswordCheck.errorMessage,
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, global: e.message }));
      }
    },
  });
  const passwordChecker = usePasswordChecker();

  const passwordStatus = passwordChecker(values.newPassword);

  const submit = (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    const validationErrors = validate(values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    changePassword();
  };

  const onModalClose = () => {
    setErrors({});
    setValues(emptyState);
    setModalVisible(false);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
          <CardDescription>Update your account password.</CardDescription>
          <Button variant="secondary" onClick={() => setModalVisible(true)}>
            Update password
          </Button>
        </CardHeader>
      </Card>
      <Modal isOpen={modalVisible} onClose={onModalClose} size="xl">
        <ModalOverlay />
        <Form onSubmit={submit} noValidate>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Update Password</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {errors.global && <Alert status="error">{errors.global}</Alert>}
              <FormControl isInvalid={!!errors.oldPassword}>
                <FormLabel>Current Password</FormLabel>
                <Input
                  type="password"
                  value={values.oldPassword}
                  name="oldPassword"
                  onChange={(event) => {
                    const oldPassword = event.target.value;
                    setValues((oldValues) => ({
                      ...oldValues,
                      oldPassword,
                    }));
                    setErrors((oldErrors) => ({
                      ...oldErrors,
                      oldPassword: undefined,
                    }));
                  }}
                />
                <FormErrorMessage>{errors.oldPassword}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.newPassword}>
                <FormLabel>New password</FormLabel>
                <Input
                  type="password"
                  value={values.newPassword}
                  name="newPassword"
                  onChange={(event) => {
                    const newPassword = event.target.value;
                    setValues((oldValues) => ({
                      ...oldValues,
                      newPassword,
                    }));
                    setErrors((oldErrors) => ({
                      ...oldErrors,
                      newPassword: undefined,
                    }));
                  }}
                />
                {passwordStatus.isWarning && !errors.newPassword && (
                  <FormHelperText iconType="warning">
                    {passwordStatus.errorMessage}
                  </FormHelperText>
                )}
                <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onModalClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={loading}
                  loadingText="Update password"
                >
                  Update Password
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Form>
      </Modal>
    </>
  );
}
